<template>
  <b-modal
    ref="modal"
    :id="modalId"
    :title="title"
    size="md"
    no-close-on-backdrop
    no-close-on-esc
  >
    <div class="form-group">
      <div class="radio-group">
        <b-form-radio
          v-for="option in options"
          :key="option.value"
          v-model="selectedValue"
          :name="radioName"
          :value="option.value"
          :disabled="disabledOptions.includes(option.value)"
        >
          <span :class="{ 'text-muted': disabledOptions.includes(option.value) }">
            {{ option.text }}
            <small v-if="disabledOptions.includes(option.value)" class="ml-1 text-danger">(Not allowed)</small>
          </span>
        </b-form-radio>
      </div>
      <div v-if="validationMessage" class="alert alert-warning mt-3 mb-0" role="alert">
        <i class="fas fa-exclamation-triangle mr-1"></i>{{ validationMessage }}
      </div>
    </div>
    <template #modal-footer>
      <b-button variant="secondary" @click="cancel">Cancel</b-button>
      <b-button variant="primary" @click="save">Save</b-button>
    </template>
  </b-modal>
</template>

<script>
export default {
    name: 'UpdateOptionModal',
    props: {
        title: { type: String, required: true },
        options: { type: Array, required: true },
        modalId: { type: String, default: 'updateOptionModal' },
        radioName: { type: String, default: 'updateOption' },
        selectedRowStatuses: { type: Array, default: () => [] },
    },
    data() {
        return {
            selectedValue: '',
            validationMessage: '',
        }
    },
    computed: {
        disabledOptions() {
            if (!this.selectedRowStatuses || this.selectedRowStatuses.length === 0) return []
            const disabled = []
            const hasCompleted = this.selectedRowStatuses.includes('Completed')
            const hasInProgress = this.selectedRowStatuses.includes('In Progress')
            const uniqueStatuses = [...new Set(this.selectedRowStatuses)]
            if (hasCompleted) {
                disabled.push('Not Acknowledged')
                disabled.push('In Progress')
            } else if (hasInProgress) {
                disabled.push('Not Acknowledged')
            }
            if (uniqueStatuses.length === 1) {
                disabled.push(uniqueStatuses[0])
            }
            return disabled
        },
    },
    methods: {
        forceRerender() {
            this.selectedValue = ''
            this.validationMessage = ''
            this.$refs.modal.show()
        },
        validateStatusTransition(newStatus) {
            if (!this.selectedRowStatuses || this.selectedRowStatuses.length === 0) {
                return ''
            }
            const uniqueStatuses = [...new Set(this.selectedRowStatuses)]
            if (uniqueStatuses.length === 1 && uniqueStatuses[0] === newStatus) {
                return `All selected record(s) already have status "${newStatus}". Please choose a different status.`
            }
            const statusOrder = { 'Not Acknowledged': 1, 'In Progress': 2, 'Completed': 3 }
            const newOrder = statusOrder[newStatus]
            if (!newOrder) return ''

            const conflicting = []
            this.selectedRowStatuses.forEach(currentStatus => {
                const currentOrder = statusOrder[currentStatus]
                if (currentOrder && currentOrder > newOrder && !conflicting.includes(currentStatus)) {
                    conflicting.push(currentStatus)
                }
            })

            if (conflicting.length === 0) return ''

            return `Cannot change status to "${newStatus}". Some selected record(s) currently have status: ${conflicting.join(', ')}. Status cannot be moved backward.`
        },
        save() {
            this.validationMessage = ''
            if (!this.selectedValue) {
                this.validationMessage = 'Please select an option before saving.'
                return
            }
            const error = this.validateStatusTransition(this.selectedValue)
            if (error) {
                this.validationMessage = error
                return
            }
            this.$emit('save', this.selectedValue)
            this.$refs.modal.hide()
        },
        cancel() {
            this.validationMessage = ''
            this.$refs.modal.hide()
        },
    },
}
</script>

<style scoped>
.radio-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
</style>
